import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import getLoyaltyPrizes from '../../../api/graphql/queries/getLoyaltyPrizes';
import LoadIcon from '../../LoadIcon';
import LoyaltyPrizesTable from './LoyaltyPrizesTable';
import LoyaltyPrizeDrawer from './LoyaltyPrizeDrawer';
import LoyaltyImportModal from './LoyaltyImportModal';
import {
  PRIZE_STATUS, sortPrizes, filterPrizes, seasonOf, pageSlice,
} from '../../../utils/loyaltyPrizes';

const DEFAULT_FILTERS = { search: '', season: '', status: '' };

// Newest import first is the default: an operator landing on this view right after
// a CSV import wants to see what just landed, not the conversion ranking. Conversion
// stays fully sortable (and keeps its low-sample guard below) — this only changes
// what the view opens on.
const DEFAULT_SORTING = ['lastUploadedAt', -1];

// Client-side pagination (Task 10). getLoyaltyPrizes deliberately returns the
// whole catalogue in one request — see the module-level comment on `query`
// state below for why paginating the request itself is not an option here.
const PAGE_SIZE = 20;

// A conversion computed on a handful of wins is noise, not a rate: three prizes
// sit at "50%" on two wins each. They still show their true percentage, but they
// do not get to head the ranking that answers "which prize works".
const MIN_WINS_FOR_RANKING = 10;

const STATUS_OPTIONS = Object.keys(PRIZE_STATUS).map(key => (
  { value: key, label: PRIZE_STATUS[key].label }
));

// sortPrizes() (locked, untouched) gives the true conversion order. When conversion is
// the active sort, we then group low-sample prizes (won < MIN_WINS_FOR_RANKING) after
// everyone else, in both directions — Array.prototype.sort is stable in every engine
// this admin runs on, so re-sorting only on the low/high grouping preserves the
// conversion order sortPrizes already established within each group. Do not collapse
// this into a single comparator that also compares conversion — that would break the
// stability this relies on.
const rankPrizes = (rows, field, direction) => {
  const sorted = sortPrizes(rows, field, direction);
  if (field !== 'conversion') return sorted;

  return [...sorted].sort((a, b) => {
    const aLow = (a.won || 0) < MIN_WINS_FOR_RANKING;
    const bLow = (b.won || 0) < MIN_WINS_FOR_RANKING;
    if (aLow === bLow) return 0;
    return aLow ? 1 : -1;
  });
};

const LoyaltyPrizesView = ({ onSelectPrizeWinners }) => {
  const [prizes, setPrizes] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // filters + sorting + page kept in ONE state object, updated atomically. Same
  // reasoning as LoyaltyContainer.jsx:74-79 — this app is on React 16 semantics and
  // does not auto-batch state updates fired outside a synthetic handler, so splitting
  // these into separate setState calls would leave a stale intermediate render in
  // between (and, for page specifically, would fire it out of step with the
  // filter/sort change that must reset it in the same render).
  const [query, setQuery] = useState({
    filters: DEFAULT_FILTERS,
    sorting: DEFAULT_SORTING,
    page: 0,
  });
  const { filters, sorting, page } = query;

  // Drawer selection is its own, single piece of state — unlike filters/sorting above it
  // is never updated in tandem with anything else in the same handler, so there is no
  // pair of setState calls that needs to land in one render and nothing to batch.
  const [selectedPrize, setSelectedPrize] = useState(null);

  // Import modal state (Task 9). `importFor` is `null` while closed, an empty object
  // `{}` when opened unscoped from this view's header ("Import codes"), or a prize row
  // when opened pre-scoped from the drawer's "Add codes" button — kept as its own piece
  // of state for the same reason `selectedPrize` is: it is never updated in tandem with
  // filters/sorting in one handler.
  const [importFor, setImportFor] = useState(null);

  const loadPrizes = async () => {
    const data = await getLoyaltyPrizes();
    setPrizes(data);
    setIsLoading(false);
    return data;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const data = await getLoyaltyPrizes();
      if (mounted) {
        setPrizes(data);
        setIsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Refetch getLoyaltyPrizes once an import completes so the ledger reflects the new
  // stock, and — when the import was launched from the drawer — swap the drawer's
  // pinned row for the refreshed one so its Figures section stops showing stale counts.
  const handleImported = async () => {
    const data = await loadPrizes();
    if (selectedPrize && data && data.items) {
      const updated = data.items.find(item => item.slugName === selectedPrize.slugName);
      if (updated) setSelectedPrize(updated);
    }
  };

  // Drawer's "See the fans who won this" action: hand the slug up to the container to
  // cross-filter the leaderboard (see LoyaltyContainer.jsx's handleSelectPrizeWinners),
  // then close the drawer, whose open/closed state (selectedPrize) lives only here.
  const handleSeeWinners = (row) => {
    onSelectPrizeWinners(row.slugName);
    setSelectedPrize(null);
  };

  // Reset to page 0 whenever a filter changes — otherwise a user on a later page who
  // types in the search box lands on a page that is now empty and concludes there are
  // no results.
  const handleFilterChange = (partial) => {
    setQuery(prev => ({ ...prev, filters: { ...prev.filters, ...partial }, page: 0 }));
  };

  // Same reset on sort — re-ranking the set from under a mid-list page reads exactly
  // like the filter case above.
  const handleSort = (field) => {
    setQuery((prev) => {
      const [prevField, prevDirection] = prev.sorting;
      let nextDirection = -1;
      if (prevField === field) {
        nextDirection = prevDirection === 1 ? -1 : 1;
      }
      return {
        ...prev, sorting: [field, nextDirection], page: 0,
      };
    });
  };

  if (isLoading) return <LoadIcon />;

  const isAuth = !!(prizes && prizes.isAuth);
  if (!isAuth) {
    return <div>You are not authorized to view the Loyalty dashboard.</div>;
  }

  const items = prizes.items || [];
  const seasonOptions = Array.from(
    new Set(items.map(item => seasonOf(item.slugName)).filter(Boolean)),
  ).sort().reverse();

  // The whole filtered + sorted set — ranking (rankPrizes) has to see every matching
  // row, not just the visible page, or the order it produces is meaningless. The page
  // slice below is a display-only window onto this.
  const filteredRows = rankPrizes(filterPrizes(items, filters), sorting[0], sorting[1]);
  const filteredCount = filteredRows.length;
  const pageCount = filteredCount === 0 ? 0 : Math.ceil(filteredCount / PAGE_SIZE);

  // Guard against a stale page: if the active filter/sort shrank the set out from
  // under the current page (e.g. an import just changed `items`), clamp to the last
  // valid page rather than render an empty table. Derived for rendering only — the
  // Prev/Next handlers below close over this same value, so the very next click
  // writes the corrected page back into state.
  const effectivePage = pageCount > 0 && page >= pageCount ? pageCount - 1 : page;
  const rows = pageSlice(filteredRows, effectivePage, PAGE_SIZE);

  const showPager = filteredCount > PAGE_SIZE;
  const rangeFrom = filteredCount === 0 ? 0 : (effectivePage * PAGE_SIZE) + 1;
  const rangeTo = Math.min((effectivePage + 1) * PAGE_SIZE, filteredCount);

  const handlePrevPage = () => {
    setQuery(prev => ({ ...prev, page: Math.max(0, effectivePage - 1) }));
  };

  const handleNextPage = () => {
    setQuery(prev => ({ ...prev, page: Math.min(pageCount - 1, effectivePage + 1) }));
  };

  const noCodesCount = items.filter(item => item.status === 'no-codes').length;
  const soldOutCount = items.filter(item => item.status === 'sold-out').length;
  const lowStockCount = items.filter(item => item.status === 'low-stock').length;

  return (
    <div className="panel">
      <div className="panel-h">
        <span className="ph-t">Prizes</span>
        <span className="spacer" />
        <button type="button" className="btn p" onClick={() => setImportFor({})}>Import codes</button>
      </div>
      <div className="ledger-summary">
        <span>
          <b>{items.length}</b>
          {' prizes'}
        </span>
        <span className="bad">
          <b>{noCodesCount}</b>
          {' no codes uploaded'}
        </span>
        <span className="bad">
          <b>{soldOutCount}</b>
          {' sold out'}
        </span>
        <span className="warn">
          <b>{lowStockCount}</b>
          {' low stock'}
        </span>
        {sorting[0] === 'conversion' ? (
          <span>
            {`Prizes with fewer than ${MIN_WINS_FOR_RANKING} wins rank last when sorting by conversion.`}
          </span>
        ) : null}
      </div>

      <div className="filters">
        <div className="search">
          <span>⌕</span>
          <input
            type="text"
            placeholder="Search prize or scope…"
            value={filters.search}
            onChange={event => handleFilterChange({ search: event.target.value })}
            style={{
              border: 'none', background: 'transparent', outline: 'none', flex: 1, font: 'inherit', color: 'inherit',
            }}
          />
        </div>

        <select
          className="drop"
          value={filters.season}
          onChange={event => handleFilterChange({ season: event.target.value })}
          aria-label="Filter by season"
        >
          <option value="">Season · Any</option>
          {seasonOptions.map(season => <option key={season} value={season}>{season}</option>)}
        </select>

        <select
          className="drop"
          value={filters.status}
          onChange={event => handleFilterChange({ status: event.target.value })}
          aria-label="Filter by status"
        >
          <option value="">Status · Any</option>
          {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>

        <span className="spacer" />
        <span className="ph-s">
          {filteredCount}
          {' of '}
          {items.length}
          {' prizes'}
        </span>
      </div>

      <LoyaltyPrizesTable
        items={rows}
        sorting={sorting}
        onSort={handleSort}
        onSelectPrize={setSelectedPrize}
      />

      {showPager ? (
        <div className="pager">
          <span>
            {`Showing ${rangeFrom}–${rangeTo} of ${filteredCount} prizes`}
          </span>
          <span>
            <button type="button" className="pg" disabled={effectivePage <= 0} onClick={handlePrevPage}>&lsaquo; Prev</button>
            &nbsp;
            <button type="button" className="pg" disabled={effectivePage >= pageCount - 1} onClick={handleNextPage}>Next &rsaquo;</button>
          </span>
        </div>
      ) : null}

      {selectedPrize ? (
        <LoyaltyPrizeDrawer
          row={selectedPrize}
          onClose={() => setSelectedPrize(null)}
          onAddCodes={row => setImportFor(row)}
          onSeeWinners={handleSeeWinners}
        />
      ) : null}

      {importFor ? (
        <LoyaltyImportModal
          prize={importFor.slugName ? importFor : null}
          onClose={() => setImportFor(null)}
          onImported={handleImported}
        />
      ) : null}
    </div>
  );
};

LoyaltyPrizesView.propTypes = {
  onSelectPrizeWinners: PropTypes.func.isRequired,
};

export default LoyaltyPrizesView;
